// Vacuum.js - Aspirador Roborock
//
// Ao contrário da MELCloud ou da Home Connect, este fala connosco pela rede
// local: o estado chega em menos de meio segundo e não depende da internet.
// Por isso o intervalo pode ser curto sem custo nenhum.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bot, Play, Pause, Square, Home, Volume2, BatteryCharging,
  Activity, Droplets, AlertCircle,
} from 'lucide-react';
import ViewHeader from './ViewHeader';

const API_URL = '/api';

function authHeaders(extra = {}) {
  const token = localStorage.getItem('access_token');
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// Enquanto está a limpar interessa acompanhar; parado na base, não muda nada.
const INTERVALO_ATIVO_MS = 5000;
const INTERVALO_PARADO_MS = 30000;

// Depois de um comando o aparelho demora a mudar de estado; reler logo
// mostrava o estado anterior.
const ESPERA_APOS_COMANDO_MS = 1500;

// Os códigos são da Roborock. Mopa é um modo à parte, não uma velocidade.
const VELOCIDADES = [101, 102, 103, 104];

// Estados em que o aspirador está a trabalhar de facto.
const A_TRABALHAR = ['a_limpar', 'limpeza_localizada', 'limpeza_de_zona',
  'limpeza_de_divisao', 'a_voltar_a_base', 'ir_para_ponto', 'a_voltar_para_lavar'];

function Vacuum({ onBack }) {
  const { t } = useTranslation();
  const [estado, setEstado] = useState(null);
  const [consumiveis, setConsumiveis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/roborock/status`, { headers: authHeaders() });
      const dados = await r.json();
      if (!r.ok) {
        setError(dados.detail || t('aspirador.erroEstado'));
        return;
      }
      setEstado(dados);
      setError('');
    } catch (err) {
      setError(t('aspirador.erroEstado'));
    }
  }, [t]);

  const carregarConsumiveis = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/roborock/consumables`, { headers: authHeaders() });
      if (r.ok) setConsumiveis(await r.json());
    } catch (err) {
      // Os consumíveis são acessórios: não vale um erro no ecrã.
    }
  }, []);

  const aTrabalhar = A_TRABALHAR.includes(estado?.state_key);

  useEffect(() => {
    carregar();
    carregarConsumiveis();
  }, [carregar, carregarConsumiveis]);

  useEffect(() => {
    const ms = aTrabalhar ? INTERVALO_ATIVO_MS : INTERVALO_PARADO_MS;
    const intervalo = setInterval(carregar, ms);
    return () => clearInterval(intervalo);
  }, [aTrabalhar, carregar]);

  const enviar = async (caminho, corpo) => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/roborock/${caminho}`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(corpo),
      });
      const dados = await r.json();
      if (!r.ok) setError(dados.detail || t('aspirador.erroComando'));
      else setTimeout(carregar, ESPERA_APOS_COMANDO_MS);
    } catch (err) {
      setError(t('aspirador.erroComando'));
    } finally {
      setLoading(false);
    }
  };

  const comando = (action) => enviar('command', { action });
  const velocidade = (level) => enviar('fan', { level });

  const duracao = (segundos) => {
    if (!segundos) return '—';
    const h = Math.floor(segundos / 3600);
    const m = Math.round((segundos % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const offline = estado && estado.connected && !estado.online;
  const semConta = estado && !estado.connected;

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={Bot}
          iconClass="icon-heatpump"
          titulo={t('aspirador.titulo')}
          subtitulo={t('aspirador.subtitulo')}
          onBack={onBack}
          onRefresh={carregar}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!estado ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('aspirador.aCarregar')}</p>
          </div>
        ) : semConta ? (
          <div className="loading-container">
            <Bot size={48} />
            <p>{t('aspirador.semConta')}</p>
          </div>
        ) : (
          <div className="heatpump-container">
            <div className={`heatpump-status-card ${aTrabalhar ? 'online' : ''}`}>
              <div className="heatpump-status-header">
                <div className="status-indicator">
                  <div className={`status-dot ${estado.online ? 'online' : 'offline'}`} />
                  <span>{estado.name}</span>
                </div>
                <div className={`power-badge ${aTrabalhar ? 'on' : 'off'}`}>
                  {offline
                    ? t('comum.offline')
                    : t(`aspirador.estados.${estado.state_key}`, { defaultValue: estado.state_key })}
                </div>
              </div>

              {!offline && (
                <div className="temp-display-section">
                  <div className="temp-display-main">
                    <BatteryCharging size={48} className="temp-icon" />
                    <div className="temp-reading">
                      <span className="temp-value">{estado.battery}%</span>
                      <span className="temp-label">{t('aspirador.bateria')}</span>
                    </div>
                  </div>
                  <div className="temp-divider" />
                  <div className="temp-display-target">
                    <Activity size={40} className="target-icon" />
                    <div className="temp-reading">
                      <span className="temp-value">{estado.clean_area_m2} m²</span>
                      <span className="temp-label">{t('aspirador.ultimaLimpeza')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {offline ? (
              <div className="heatpump-info-card">
                <p className="section-help">{t('aspirador.naoResponde')}</p>
              </div>
            ) : (
              <>
                <div className="heatpump-controls-card">
                  <h3>{t('comum.controlos')}</h3>
                  <div className="control-section">
                    <div className="temp-quick-buttons">
                      <button
                        className={`temp-quick-btn ${aTrabalhar ? 'active' : ''}`}
                        onClick={() => comando('start')}
                        disabled={loading}
                      >
                        <Play size={16} /> {t('aspirador.limpar')}
                      </button>
                      <button className="temp-quick-btn" onClick={() => comando('pause')} disabled={loading}>
                        <Pause size={16} /> {t('aspirador.pausar')}
                      </button>
                      <button className="temp-quick-btn" onClick={() => comando('stop')} disabled={loading}>
                        <Square size={14} /> {t('aspirador.parar')}
                      </button>
                      <button className="temp-quick-btn" onClick={() => comando('dock')} disabled={loading}>
                        <Home size={16} /> {t('aspirador.base')}
                      </button>
                      <button className="temp-quick-btn" onClick={() => comando('find')} disabled={loading}>
                        <Volume2 size={16} /> {t('aspirador.encontrar')}
                      </button>
                    </div>
                  </div>

                  <div className="control-section">
                    <div className="control-header">
                      <Activity size={20} />
                      <span>{t('aspirador.sucao')}</span>
                    </div>
                    <div className="temp-quick-buttons">
                      {VELOCIDADES.map((nivel) => (
                        <button
                          key={nivel}
                          className={`temp-quick-btn ${estado.fan_power === nivel ? 'active' : ''}`}
                          onClick={() => velocidade(nivel)}
                          disabled={loading}
                        >
                          {t(`aspirador.velocidades.${nivel}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="heatpump-info-card">
                  <h3>{t('comum.informacoes')}</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <Activity size={20} />
                      <div>
                        <span className="info-label">{t('aspirador.duracao')}</span>
                        <span className="info-value">{duracao(estado.clean_time_s)}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <Droplets size={20} />
                      <div>
                        <span className="info-label">{t('aspirador.depositoAgua')}</span>
                        <span className="info-value">
                          {estado.water_box ? t('aspirador.colocado') : t('aspirador.ausente')}
                        </span>
                      </div>
                    </div>
                    <div className="info-item">
                      <Bot size={20} />
                      <div>
                        <span className="info-label">{t('comum.modelo')}</span>
                        <span className="info-value">{estado.model || '—'}</span>
                      </div>
                    </div>
                    {estado.error_code > 0 && (
                      <div className="info-item">
                        <AlertCircle size={20} />
                        <div>
                          <span className="info-label">{t('comum.erro')}</span>
                          <span className="info-value">{estado.error_code}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {consumiveis && (
                  <div className="heatpump-info-card">
                    <h3>{t('aspirador.consumiveis')}</h3>
                    <div className="info-grid">
                      {consumiveis.items.map((item) => (
                        <div className="info-item" key={item.key}>
                          {item.overdue ? <AlertCircle size={20} /> : <Activity size={20} />}
                          <div>
                            <span className="info-label">{t(`aspirador.pecas.${item.key}`)}</span>
                            {/* Horas de uso, não percentagem: quem nunca reinicia
                                os contadores na app da Roborock veria só "0%"
                                em tudo, o que não diz nada. */}
                            <span className="info-value">
                              {t('aspirador.horasDe', {
                                usadas: item.hours_used,
                                total: item.hours_total,
                              })}
                            </span>
                            {item.overdue && (
                              <span className="info-label" style={{ opacity: 0.9 }}>
                                {t('aspirador.acimaDoRecomendado')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="section-help" style={{ marginTop: '1rem' }}>
                      {t('aspirador.notaConsumiveis')}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Vacuum;
